import _ from 'underscore';
import React, { useContext, useState, useCallback, useEffect, Fragment } from 'react';
import { CivilDate } from 'tc39-proposal-temporal';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DefaultButton, PrimaryButton } from '@bingads-webui-react/fabric-control';
import { Separator } from '@fluentui/react';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { storeSeasonalityAdjustmentName, getSeasonalityAdjustmentName } from '@bingads-webui-campaign/seasonality-id-name-utils';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { SeasonalityTextInput } from './seasonality-text-input';
import { SeasonalityScopeChoice, validateScope } from './seasonality-scope-choice';
import { SeasonalityDeviceChoice, validateDevices } from './seasonality-device-choice';
import { SeasonalityAdjustmentRateInput, validateConversionRate } from './seasonality-adjustment-rate-input';
import { SeasonalityDateTimePicker, validateDateTimeRange } from './seasonality-date-time-picker';
import { localizeApiError } from '../i18n-map/localize-api-errors';
import { Error } from './error';
import { AdjustmentType, isDataExclusion } from '../adjustment-type';

function getStyles() {
  return {
    saveButton: {
      marginRight: 8,
    },
  };
}

function SeasonalityAdjustmentCreationPanel(props) {
  const {
    onCancel,
    onSuccess,
    seasonalityId,
    isUpdate,
    scope,
    classes,
    adjustmentType,
    permissions,
  } = props;
  const i18n = useContext(I18nContext);
  const campaignTypes = ['Default', 'Shopping'];

  if (permissions.dynamic.PerformanceMaxCampaigns) {
    campaignTypes.push('PerformanceMax');
  }

  if (permissions.IsDataExclusionForAudienceCampaignEnabled && isDataExclusion(adjustmentType)) {
    campaignTypes.push('Audience');
  }

  const [seasonalityName, setSeasonalityName] = useState('');
  const [seasonalityDescription, setSeasonalityDescription] = useState('');
  const [selectedStartDate, setSelectedStartDate] = useState(undefined);
  const [selectedEndDate, setSelectedEndDate] = useState(undefined);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [selectedScope, setSelectedScope] = useState('type');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [isValidText, setIsValidText] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [selectedCampaignTypes, setSelectedCampaignTypes] = useState(campaignTypes);
  const [selectedDevices, setSelectedDevices] = useState(['Computers', 'Smartphones', 'Tablets']);
  const [selectedSymbol, setSelectedSymbol] = useState('increase');
  const initialAdjustmentRate = isDataExclusion(adjustmentType) ? '0' : '';
  const [adjustmentRate, setAdjustmentRate] = useState(initialAdjustmentRate);
  const [isValidRate, setIsValidRate] = useState(true);
  const [isLoading, setIsLoading] = useState(isUpdate);
  const [apiError, setApiError] = useState('');

  /* istanbul ignore next */
  useEffect(() => {
    if (isUpdate) {
      setIsLoading(true);

      jsdata.SeasonalityAdjustment.find(seasonalityId).then((response) => {
        setSeasonalityName(response.Name);
        setSeasonalityDescription(response.Description);
        setSelectedDevices(response.DeviceTypeFilter);
        const startDate = i18n.dateParser({
          raw: 'yyyy-MM-ddTHH:mm:ss',
        })(response.StartDate.substr(0, 19));
        const endDate = i18n.dateParser({
          raw: 'yyyy-MM-ddTHH:mm:ss',
        })(response.EndDate.substr(0, 19));

        setSelectedStartDate(new CivilDate(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          startDate.getDate()
        ));
        setSelectedEndDate(new CivilDate(
          endDate.getFullYear(),
          endDate.getMonth() + 1,
          endDate.getDate()
        ));

        setSelectedStartTime(startDate.getHours().toString());
        setSelectedEndTime(endDate.getHours().toString());
        setSelectedScope(_.isEmpty(response.CampaignTypeFilter) ? 'specific' : 'type');
        setSelectedCampaignTypes(response.CampaignTypeFilter);
        setSelectedCampaigns(_.map(response.CampaignAssociations, cmp => ({
          Id: cmp.CampaignId,
          name: cmp.CampaignName,
          CampaignType: cmp.CampaignType,
        })));
        setSelectedSymbol(response.AdjustmentPercentage > 0 ? 'increase' : 'decrease');
        setAdjustmentRate(Math.abs(response.AdjustmentPercentage).toString());
      }).catch((response) => {
        const fetchErrorMsg = localizeApiError(response, i18n, adjustmentType);
        notificationApi.showWarning(fetchErrorMsg);
        onSuccess();
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [i18n, isUpdate, onSuccess, scope, seasonalityId]);

  const onSelectedCampaignTypeChanged = useCallback((event, item) => {
    setSelectedCampaignTypes(item ? [...selectedCampaignTypes, event.target.name] :
      _.without(selectedCampaignTypes, event.target.name));
  }, [selectedCampaignTypes]);

  const onSelectedDevicesChanged = useCallback((event, item) => {
    setSelectedDevices(item ? [...selectedDevices, event.target.name] :
      _.without(selectedDevices, event.target.name));
  }, [selectedDevices]);

  const validate = useCallback(() => {
    const validators = [
      () => isValidText,
      () => isValidRate,
      () => validateScope({ selectedScope, selectedCampaignTypes, selectedCampaigns }),
      () => validateDevices(selectedDevices),
      () => validateConversionRate(selectedSymbol, adjustmentRate),
      () => validateDateTimeRange(selectedStartDate, selectedStartTime, selectedEndDate, selectedEndTime),
    ];
    const validationResults = validators.map(fn => fn());
    const allValid = _.all(validationResults);
    if (!allValid) {
      setShowValidationError(true);
    }
    return allValid;
  }, [adjustmentRate, isValidRate, isValidText,
    selectedCampaignTypes, selectedCampaigns,
    selectedDevices, selectedEndDate, selectedEndTime,
    selectedScope, selectedStartDate, selectedStartTime, selectedSymbol]);

  /* istanbul ignore next */
  const onSubmit = useCallback(() => {
    if (!validate()) {
      return;
    }

    const startDateTime = new Date(selectedStartDate);
    const endDateTime = new Date(selectedEndDate);
    const convertedCampaigns = selectedCampaigns.map(item => ({
      CampaignId: item.Id,
    }));

    startDateTime.setUTCHours(selectedStartTime);
    endDateTime.setUTCHours(selectedEndTime);

    const data = {
      Name: seasonalityName,
      Description: seasonalityDescription,
      AdjustmentPercentage: selectedSymbol === 'increase' ? adjustmentRate : `-${adjustmentRate}`,
      DeviceTypeFilter: selectedDevices,
      StartDate: startDateTime.toISOString(),
      EndDate: endDateTime.toISOString(),
      AdjustmentType: adjustmentType,
    };

    // extend for edit
    if (isUpdate) {
      _.extend(data, {
        Id: seasonalityId,
      });
    }

    if (selectedScope === 'type') {
      _.extend(data, {
        CampaignTypeFilter: selectedCampaignTypes,
      });
    } else {
      _.extend(data, {
        CampaignAssociations: convertedCampaigns,
        CampaignAssociationsCount: _.size(selectedCampaigns),
      });
    }

    setIsLoading(true);
    setApiError('');

    const jsdataCall = jsdata.SeasonalityAdjustment.create(data);
    jsdataCall
      .then(() => {
        if (isUpdate) {
          const storedName = getSeasonalityAdjustmentName(seasonalityId);
          if (storedName && (storedName !== seasonalityName)) {
            storeSeasonalityAdjustmentName({ id: seasonalityId, name: seasonalityName });
          }
        }
        onSuccess();
      })
      .catch((response) => {
        if (!isUpdate && (Number(response.entity.Id) > 0)) {
          onSuccess();
        }
        setApiError(localizeApiError(response, i18n, adjustmentType));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [adjustmentRate, i18n, isUpdate, onSuccess, adjustmentType,
    seasonalityDescription, seasonalityId,
    seasonalityName, selectedCampaignTypes,
    selectedCampaigns, selectedDevices,
    selectedEndDate, selectedEndTime, selectedScope,
    selectedStartDate, selectedStartTime, selectedSymbol, validate]);

  return (
    <div className={classNames('panel', 'seasonality-creation-panel')}>
      <UiBlocker
        blocking={isLoading}
        loadingText={i18n.getString(_TL_('Loading...'))}
        classes={{
          blockUiContainer: 'blocker-global',
          blockerIndicator: 'blocker-indicator',
        }}
      >
        <div className="panel-heading" role="heading" aria-level="2">
          <div className="panel-title">
            {i18n.getString(isDataExclusion(adjustmentType) ? _TL_('Create a data exclusion') : _TL_('Create a seasonality adjustment'))}
          </div>
        </div>
        <div className="panel-body">
          <SeasonalityTextInput
            adjustmentType={adjustmentType}
            onChangeName={setSeasonalityName}
            name={seasonalityName}
            description={seasonalityDescription}
            onChangeDescription={setSeasonalityDescription}
            onValidate={setIsValidText}
            showValidationError={showValidationError}
          />
          <SeasonalityDateTimePicker
            adjustmentType={adjustmentType}
            setSelectedStartDate={setSelectedStartDate}
            selectedStartDate={selectedStartDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedEndDate={selectedEndDate}
            selectedStartTime={selectedStartTime}
            setSelectedStartTime={setSelectedStartTime}
            selectedEndTime={selectedEndTime}
            setSelectedEndTime={setSelectedEndTime}
            showValidationError={showValidationError}
            i18n={i18n}
          />
          <Separator />
          <SeasonalityScopeChoice
            adjustmentType={adjustmentType}
            selectedScope={selectedScope}
            setSelectedScope={setSelectedScope}
            selectedCampaignTypes={selectedCampaignTypes}
            selectedCampaigns={selectedCampaigns}
            onSelectCampaigns={setSelectedCampaigns}
            onSelectedCampaignTypeChanged={onSelectedCampaignTypeChanged}
            showValidationError={showValidationError}
            i18n={i18n}
          />
          <SeasonalityDeviceChoice
            adjustmentType={adjustmentType}
            selectedDevices={selectedDevices}
            onSelectedDevicesChanged={onSelectedDevicesChanged}
            showValidationError={showValidationError}
            i18n={i18n}
          />
          {adjustmentType === AdjustmentType.SeasonalityAdjustment &&
            <Fragment>
              <Separator />
              <SeasonalityAdjustmentRateInput
                selectedSymbol={selectedSymbol}
                setSelectedSymbol={setSelectedSymbol}
                setAdjustmentRate={setAdjustmentRate}
                onValidate={setIsValidRate}
                adjustmentRate={adjustmentRate}
                showValidationError={showValidationError}
                i18n={i18n}
              />
            </Fragment>
          }
          <Error message={apiError} />
        </div>
        <div className="panel-footer">
          <PrimaryButton
            className={classNames('save-button', classes.saveButton)}
            disabled={isLoading}
            text={i18n.getString(_TL_('Save'))}
            onClick={onSubmit}
          />
          <DefaultButton className="cancel-button" text={i18n.getString(_TL_('Cancel'))} onClick={onCancel} />
        </div>
      </UiBlocker>
    </div>
  );
}

SeasonalityAdjustmentCreationPanel.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  seasonalityId: PropTypes.number,
  isUpdate: PropTypes.bool,
  scope: PropTypes.shape(),
  adjustmentType: PropTypes.oneOf([AdjustmentType.DataExclusion, AdjustmentType.SeasonalityAdjustment]),
  classes: PropTypes.shape().isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      PerformanceMaxCampaigns: PropTypes.bool,
    }),
  }).isRequired,
};

SeasonalityAdjustmentCreationPanel.defaultProps = {
  onSuccess: _.noop,
  onCancel: _.noop,
  seasonalityId: undefined,
  isUpdate: false,
  scope: undefined,
  adjustmentType: AdjustmentType.SeasonalityAdjustment,
};

const Wrapped = withPermissions(withDefaultStyles(SeasonalityAdjustmentCreationPanel, getStyles));

export { Wrapped as SeasonalityAdjustmentCreationPanel };
